import type { ReactElement } from "react";
import { createBrowserRouter, Navigate, type RouteObject } from "react-router-dom";
import { RequireRole } from "@/features/auth/RequireRole";
import { RequirePermission } from "@/features/auth/RequirePermission";
import { LoginPage } from "@/features/auth/LoginPage";
import { AppShell } from "./layout/AppShell";
import { DashboardPage } from "@/features/dashboard/DashboardPage";
import { VenueApprovalPage } from "@/features/venues-approval/VenueApprovalPage";
import { VenuesPage } from "@/features/venues/VenuesPage";
import { VenueDetailPage } from "@/features/venues/VenueDetailPage";
import { VenueQrPrintPage } from "@/features/venues/VenueQrPrintPage";
import { PropertiesPage } from "@/features/properties/PropertiesPage";
import { PropertyDetailPage } from "@/features/properties/PropertyDetailPage";
import { DocumentsPage } from "@/features/compliance/documents/DocumentsPage";
import { DocumentDetailPage } from "@/features/compliance/documents/DocumentDetailPage";
import { TranslationEditorPage } from "@/features/compliance/documents/TranslationEditorPage";
import { RequirementsPage } from "@/features/compliance/requirements/RequirementsPage";
import { DocumentTypesPage } from "@/features/compliance/document-types/DocumentTypesPage";
import { LocalesPage } from "@/features/compliance/locales/LocalesPage";
import { CommunicationsPage } from "@/features/communications/catalog/CommunicationsPage";
import { CommunicationDetailPage } from "@/features/communications/detail/CommunicationDetailPage";
import { TranslationEditorPage as CommunicationTranslationEditorPage } from "@/features/communications/translations/TranslationEditorPage";
import { CommunicationLocalesPage } from "@/features/communications/locales/CommunicationLocalesPage";
import { UsersPage } from "@/features/users/UsersPage";
import { UserDetailPage } from "@/features/users/UserDetailPage";
import { InvitationsPage } from "@/features/invitations/InvitationsPage";
import { OrganizationDetailPage } from "@/features/organizations/OrganizationDetailPage";
import { OrganizationsPage } from "@/features/organizations/OrganizationsPage";
import { FeatureFlagsPage } from "@/features/feature-flags/FeatureFlagsPage";
import { FeatureFlagDetailPage } from "@/features/feature-flags/FeatureFlagDetailPage";
import { CatalogPage } from "@/features/catalog/CatalogPage";
import { DriftAlertsPage } from "@/features/subscriptions/DriftAlertsPage";
import { SubscriptionsPage } from "@/features/subscriptions/SubscriptionsPage";
import { SubscriptionDetailPage } from "@/features/subscriptions/SubscriptionDetailPage";
import { FoundingReportPage } from "@/features/founding/FoundingReportPage";
import { SubscriptionTiersPage } from "@/features/subscription-tiers/SubscriptionTiersPage";
import { PayoutReportPage } from "@/features/finance/PayoutReportPage";
import { OwnerPayoutsPage } from "@/features/finance/OwnerPayoutsPage";
import { LedgerCorrectionsPage } from "@/features/finance/LedgerCorrectionsPage";
import { LedgerPage } from "@/features/finance/LedgerPage";
import { AccrualsPage } from "@/features/finance/AccrualsPage";
import { FinancialReportsPage } from "@/features/finance/FinancialReportsPage";
import { ForbiddenPage } from "./pages/ForbiddenPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { Permission } from "@/shared/auth/permissions";
import { ADMIN_ROLES } from "@/shared/types/role";
import { SECTION_ROLES, Section } from "./section-access";

/**
 * Per-section route guard. The roles come from `SECTION_ROLES` (one table
 * shared with the Sidebar, mirroring the API's gates), so a section the API
 * answers 403 to is never rendered: direct navigation lands on `/403`. The
 * in-component gates on writes (Pay/Liquidate, founding assign, ...) stay.
 */
function guarded(section: Section, element: ReactElement): ReactElement {
  return <RequireRole roles={SECTION_ROLES[section]}>{element}</RequireRole>;
}

export const routes: RouteObject[] = [
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/403",
    element: <ForbiddenPage />,
  },
  // Venue QR print sheet — outside the AppShell so the sidebar/header never
  // reach the printer. Same gate as the venue directory.
  {
    path: "/venues-directory/:id/qr",
    element: guarded(Section.VENUE_DIRECTORY, <VenueQrPrintPage />),
  },
  {
    path: "/",
    element: (
      <RequireRole roles={ADMIN_ROLES}>
        <AppShell />
      </RequireRole>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: "dashboard", element: guarded(Section.DASHBOARD, <DashboardPage />) },
      { path: "venues", element: guarded(Section.VENUE_APPROVAL, <VenueApprovalPage />) },
      // General venue directory (all statuses) — separate screen from
      // the approval queue above, which stays at /venues untouched.
      {
        path: "venues-directory",
        element: guarded(Section.VENUE_DIRECTORY, <VenuesPage />),
      },
      {
        path: "venues-directory/:id",
        element: guarded(Section.VENUE_DIRECTORY, <VenueDetailPage />),
      },
      { path: "properties", element: guarded(Section.PROPERTIES, <PropertiesPage />) },
      { path: "properties/:id", element: guarded(Section.PROPERTIES, <PropertyDetailPage />) },
      { path: "users", element: guarded(Section.USERS, <UsersPage />) },
      { path: "users/:id", element: guarded(Section.USERS, <UserDetailPage />) },
      // Invitations across organizations — gated on the `org.members.read`
      // permission from /auth/me (not a role list); the API enforces it too.
      {
        path: "invitations",
        element: (
          <RequirePermission permission={Permission.ORG_MEMBERS_READ}>
            <InvitationsPage />
          </RequirePermission>
        ),
      },
      // Organizations directory and page (reached from it, from a user's
      // memberships and from an invitation) — same `org.members.read` gate;
      // the API enforces it on every read.
      {
        path: "organizations",
        element: (
          <RequirePermission permission={Permission.ORG_MEMBERS_READ}>
            <OrganizationsPage />
          </RequirePermission>
        ),
      },
      {
        path: "organizations/:organizationId",
        element: (
          <RequirePermission permission={Permission.ORG_MEMBERS_READ}>
            <OrganizationDetailPage />
          </RequirePermission>
        ),
      },
      // Legacy /tos redirects to the new compliance section so existing
      // bookmarks / sidebar links from BO-1.4 don't 404.
      { path: "tos", element: <Navigate to="/compliance/documents" replace /> },
      { path: "tos/*", element: <Navigate to="/compliance/documents" replace /> },
      { path: "compliance", element: <Navigate to="/compliance/documents" replace /> },
      { path: "compliance/documents", element: guarded(Section.COMPLIANCE, <DocumentsPage />) },
      {
        path: "compliance/documents/:id",
        element: guarded(Section.COMPLIANCE, <DocumentDetailPage />),
      },
      {
        path: "compliance/documents/:id/translations/:locale",
        element: guarded(Section.COMPLIANCE, <TranslationEditorPage />),
      },
      {
        path: "compliance/requirements",
        element: guarded(Section.COMPLIANCE, <RequirementsPage />),
      },
      {
        path: "compliance/document-types",
        element: guarded(Section.COMPLIANCE, <DocumentTypesPage />),
      },
      { path: "compliance/locales", element: guarded(Section.COMPLIANCE, <LocalesPage />) },
      // Communications template management (US-6.5).
      {
        path: "communications",
        element: guarded(Section.COMMUNICATIONS, <CommunicationsPage />),
      },
      {
        path: "communications/locales",
        element: guarded(Section.COMMUNICATIONS, <CommunicationLocalesPage />),
      },
      {
        path: "communications/:id",
        element: guarded(Section.COMMUNICATIONS, <CommunicationDetailPage />),
      },
      {
        path: "communications/:id/channels/:channelId/translations/:locale",
        element: guarded(Section.COMMUNICATIONS, <CommunicationTranslationEditorPage />),
      },
      // Subscription lookup is read-only — open to every admin role.
      { path: "subscriptions", element: guarded(Section.SUBSCRIPTIONS, <SubscriptionsPage />) },
      {
        path: "subscriptions/:id",
        element: guarded(Section.SUBSCRIPTIONS, <SubscriptionDetailPage />),
      },
      // Founding Partners cockpit — the assign/unassign mutations are
      // ADMIN-only and gated in-component.
      {
        path: "founding",
        element: guarded(Section.FOUNDING, <FoundingReportPage />),
      },
      { path: "feature-flags", element: guarded(Section.FEATURE_FLAGS, <FeatureFlagsPage />) },
      {
        path: "feature-flags/:id",
        element: guarded(Section.FEATURE_FLAGS, <FeatureFlagDetailPage />),
      },
      {
        path: "catalog",
        element: guarded(Section.CATALOG, <CatalogPage />),
      },
      {
        path: "drift-alerts",
        element: guarded(Section.DRIFT_ALERTS, <DriftAlertsPage />),
      },
      // Payouts / finance — Pay/Liquidate actions are ADMIN-gated inside
      // the components.
      { path: "finance", element: <Navigate to="/finance/payouts" replace /> },
      {
        path: "finance/payouts",
        element: guarded(Section.FINANCE, <PayoutReportPage />),
      },
      {
        path: "finance/owners/:userId/payouts",
        element: guarded(Section.FINANCE, <OwnerPayoutsPage />),
      },
      {
        path: "finance/reports",
        element: guarded(Section.FINANCE, <FinancialReportsPage />),
      },
      {
        path: "finance/accruals",
        element: guarded(Section.FINANCE, <AccrualsPage />),
      },
      {
        path: "finance/ledger",
        element: guarded(Section.FINANCE, <LedgerPage />),
      },
      {
        path: "finance/ledger-corrections",
        element: guarded(Section.FINANCE, <LedgerCorrectionsPage />),
      },
      // Subscription Tiers ABM (capacity pivot).
      {
        path: "subscription-tiers",
        element: guarded(Section.SUBSCRIPTION_TIERS, <SubscriptionTiersPage />),
      },
    ],
  },
  {
    path: "*",
    element: <NotFoundPage />,
  },
];

export const router = createBrowserRouter(routes);
